import { useMemo, useState } from "react";
import {
  ActivityIndicator,
  View,
  Text,
  TextInput,
  ScrollView,
  Platform,
  Pressable,
} from "react-native";
import Svg, { Circle } from "react-native-svg";
import {
  ArrowRight,
  ChevronLeft,
  FilePlus2,
  MessageSquare,
  Plus,
  Sparkles,
  Trophy,
  type LucideIcon,
} from "lucide-react-native";
import type { NativeStackScreenProps } from "@react-navigation/native-stack";
import type { NavigationProp } from "@react-navigation/native";
import type { CareStackParamList, MainTabParamList } from "../types";
import { useSmartBack } from "../hooks/useSmartBack";
import GlassScreenLayout from "@louise/moon-ui/primitives/GlassScreenLayout";
import {
  GlassButton,
  MoonDivider,
  Row,
  SectionTitleRow,
  Tag,
} from "@louise/moon-ui";
import {
  GLASS_CARD_STYLE,
  SCREEN_PADDING_TOP,
} from "@louise/moon-ui/theme/tokens";
import { Hero } from "@src/packages/homepage/plan/components/hero/Hero";
import { EMBER } from "../../onboarding/theme";
import HeaderActions from "../components/HeaderActions";
import ScreenTitle from "../components/ScreenTitle";
import { usePatient } from "@src/packages/patients/hooks/usePatient";
import { usePatientSyncStatus } from "@src/packages/patients/sync/usePatientSync";
import { useTranslations, type TranslationFunction } from "@src/packages/i18n";
import {
  getMedicalHistoryProgressSummary,
  getVisibleMedicalHistoryGroups,
  type MedicalHistorySection,
} from "@src/packages/medical-history/sectionRegistry";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
type Props = NativeStackScreenProps<CareStackParamList, "MedicalHistory">;

// Routes within MedicalHistory that the navigation function targets.
// Filtering to *Flow keys makes navigation.navigate(route) typecheck without
// an `any` cast since all flow routes accept `undefined` params.
type MedicalHistoryFlowRoute = Extract<
  keyof CareStackParamList,
  `${string}Flow`
>;

const careHeroGlowGradient =
  require("../../assets/images/care-hero-glow-gradient.png") as number;

const SECTION_BENEFIT_COPY: Record<string, string> = {
  "basic-info":
    "These basics help Louise personalize recommendations and avoid asking the same setup questions again.",
  infertility:
    "Diagnosis context helps explain what has already been ruled in, ruled out, or still needs follow-up.",
  "fertility-testing":
    "Testing history helps Louise match results to protocols and spot missing labs or imaging.",
  "fertility-treatments":
    "Prior cycles help Louise understand what has worked, what did not, and what to avoid repeating.",
  "menstrual-health":
    "Cycle patterns help time next steps and make symptoms easier to interpret.",
  "sexual-health":
    "Sexual health context helps surface screening, comfort, and partner factors that can affect care.",
  contraception:
    "Contraception history helps clarify timing, hormones, devices, and prior side effects.",
  pregnancies:
    "Pregnancy history helps contextualize risks, outcomes, and the support your care team may need.",
  "medications-vaccines":
    "Medication and vaccine history helps Louise check safety, interactions, and care-plan constraints.",
  allergies:
    "Allergies help Louise flag medications or materials that your care team should avoid.",
  diseases:
    "Medical conditions help Louise adapt recommendations around risks, monitoring, and specialist context.",
  surgeries:
    "Surgical history helps explain anatomy, recovery, and records your care team may need.",
  exposure:
    "Exposure details help identify environmental factors that may change testing or treatment advice.",
  substances:
    "Substance history helps Louise tailor safer recommendations without making assumptions.",
  "sleep-activity":
    "Sleep and activity patterns help contextualize energy, recovery, and lifestyle support.",
  "diet-supplements":
    "Diet and supplements help Louise spot useful habits, gaps, and interaction risks.",
  "family-history":
    "Family history helps surface inherited risks and screening considerations.",
};

type SectionRow = {
  key: string;
  title: string;
  icon: React.ReactNode;
  percent: number;
  label: string;
  recap?: string;
  onPress?: () => void;
};

type SectionGroup = {
  key: string;
  title: string;
  icon: LucideIcon;
  rows: SectionRow[];
};

const LABEL_OVERRIDES: Record<string, string> = {
  afab: "AFAB",
  amab: "AMAB",
  amh: "AMH",
  iui: "IUI",
  ivf: "IVF",
  icsi: "ICSI",
  fet: "FET",
  pcos: "PCOS",
  hpv: "HPV",
  mri: "MRI",
  mmr: "MMR",
  c_section: "C-section",
  hormonal_iud: "hormonal IUD",
  operative_laparoscopy: "operative laparoscopy",
  regular_cycle: "regular cycles",
  no_pregnancy: "no pregnancy",
  live_birth: "live birth",
  egg_freezing: "egg freezing",
  embryo_freezing: "embryo freezing",
  sperm_freezing: "sperm freezing",
  medicated_cycle: "medicated cycle",
  low_sperm_count: "low sperm count",
  neurological_mental_health: "mental health",
  folic_acid: "folic acid",
  caucasian: "Caucasian",
  asian: "Asian",
  chemicals: "chemical",
  at_least_5_times_week: "at least 5 times weekly",
  monthly_or_less: "monthly or less",
  "1_a_week": "weekly",
};

function cleanSentenceFragment(value: string): string {
  return value.trim().replace(/[.!?]+$/, "");
}

function humanizeValue(value: unknown): string {
  if (value === null || value === undefined) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const key = raw.toLowerCase();
  return LABEL_OVERRIDES[key] ?? raw.replace(/_/g, " ").replace(/\s+/g, " ");
}

function sentenceCase(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function lowerFirst(value: string): string {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  return trimmed.charAt(0).toLowerCase() + trimmed.slice(1);
}

function formatList(values: unknown[], max = 3): string {
  const labels = values
    .map((value) => cleanSentenceFragment(humanizeValue(value)))
    .filter(Boolean);

  if (labels.length === 0) return "";
  const visible = labels.slice(0, max);
  const hiddenCount = labels.length - visible.length;
  if (hiddenCount > 0) {
    return `${visible.join(", ")}, and ${hiddenCount} more`;
  }

  if (visible.length === 1) return visible[0]!;
  if (visible.length === 2) return `${visible[0]} and ${visible[1]}`;
  return `${visible.slice(0, -1).join(", ")}, and ${visible.at(-1)}`;
}

function formatCount(count: number, singular: string, plural = `${singular}s`) {
  return `${count} ${count === 1 ? singular : plural}`;
}

function getYear(value?: string | null): string | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return String(date.getFullYear());
}

const SECTION_EMPTY_COPY: Record<string, string> = {
  "basic-info":
    "Add age, body measurements, reproductive basics, allergies, and medications so Louise has the setup context.",
  infertility:
    "Add whether infertility has been diagnosed, any known causes, and whether partner factors are involved.",
  "fertility-testing":
    "Add whether fertility testing has been done, then upload or enter any lab, imaging, genetic, semen, or urine results.",
  "fertility-treatments":
    "Add whether there have been prior fertility treatments, then include cycle types, dates, clinics, and outcomes.",
  "menstrual-health":
    "Add cycle pattern and fertile-window awareness so Louise can understand timing and symptoms.",
  "sexual-health":
    "Add screening, STI, infection, pain, lubricant, and partner-ejaculation context where relevant.",
  contraception:
    "Add current or past contraception methods, dates, and any side effects or stopping context.",
  pregnancies:
    "Add pregnancy history, outcomes, delivery details, and any complications that may matter for care.",
  "medications-vaccines":
    "Add current medications, medication details, and vaccine history for safety and interaction checks.",
  allergies:
    "Add whether there are allergies and include the allergen, reaction, and treatment context when known.",
  diseases:
    "Add medical conditions, breast/cancer history where relevant, and any diagnoses or treatments.",
  surgeries:
    "Add prior surgeries, dates, and notes about what was done or why it matters.",
  exposure:
    "Add environmental or workplace exposures, frequency, and context so risks are not missed.",
  substances:
    "Add alcohol, nicotine, cannabis, or other substance history with frequency and notes.",
  "sleep-activity":
    "Add sleep range and activity habits so Louise can understand recovery and lifestyle context.",
  "diet-supplements":
    "Add diet patterns and supplements, including names, frequency, and notes.",
  "family-history":
    "Add ancestry, adoption or donor context, DNA reports, and family fertility or disease history.",
};

const SECTION_INCOMPLETE_COPY: Record<string, string> = {
  "basic-info":
    "Finish the remaining basics so Louise can interpret the rest of the history safely.",
  infertility:
    "Finish diagnosis status, causes, and partner-factor details so the fertility context is clear.",
  "fertility-testing":
    "Add missing test result details or mark that testing has not been done.",
  "fertility-treatments":
    "Finish prior-treatment answers and add cycle details if treatments happened.",
  "menstrual-health":
    "Finish cycle status and fertile-window awareness so timing can be interpreted.",
  "sexual-health":
    "Finish screening, infection, pain, lubricant, and partner context where relevant.",
  contraception:
    "Finish method details, dates, and stopping context where relevant.",
  pregnancies:
    "Finish outcomes, delivery details, and complication notes where relevant.",
  "medications-vaccines":
    "Finish medication details and vaccine history for interaction and safety checks.",
  allergies: "Finish allergen details, reactions, and treatment context.",
  diseases:
    "Finish condition categories, breast/cancer history where relevant, and diagnosis details.",
  surgeries: "Finish surgery details, dates, and notes.",
  exposure: "Finish exposure type, frequency, and context.",
  substances: "Finish substance type, frequency, quantity, and notes.",
  "sleep-activity":
    "Finish sleep and activity answers so lifestyle context is complete.",
  "diet-supplements":
    "Finish diet and supplement details, including frequency and notes.",
  "family-history":
    "Finish ancestry, adoption/donor context, DNA reports, and family fertility history.",
};

const SECTION_COMPLETE_CONTEXT: Record<string, string> = {
  "basic-info":
    "This gives Louise the setup context for recommendations, alerts, and safety checks.",
  infertility:
    "This helps Louise understand known fertility factors and partner context.",
  "fertility-testing":
    "This helps Louise match results to protocols and spot follow-up needs.",
  "fertility-treatments":
    "This helps Louise understand what has been tried, what worked, and what to avoid repeating.",
  "menstrual-health":
    "This helps Louise interpret timing, symptoms, and cycle-related patterns.",
  "sexual-health":
    "This helps Louise surface screening, comfort, and partner factors for care planning.",
  contraception:
    "This helps Louise understand hormones, devices, timing, and prior side effects.",
  pregnancies:
    "This helps Louise contextualize outcomes, risks, and provider discussion points.",
  "medications-vaccines":
    "This supports medication safety checks, vaccine context, and care-plan constraints.",
  allergies:
    "This helps Louise flag medications or materials the care team should avoid.",
  diseases:
    "This helps Louise adapt recommendations around risks, monitoring, and specialist context.",
  surgeries:
    "This helps Louise account for anatomy, recovery history, and records the care team may need.",
  exposure:
    "This helps Louise factor environmental risks into testing and treatment advice.",
  substances:
    "This helps Louise tailor safer recommendations without making assumptions.",
  "sleep-activity":
    "This helps Louise understand recovery, energy, and lifestyle support needs.",
  "diet-supplements":
    "This helps Louise spot helpful habits, gaps, and possible interaction risks.",
  "family-history":
    "This helps Louise surface inherited risks and screening considerations.",
};

function getSectionRecap(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  patient: any,
  sectionKey: string,
  t: TranslationFunction,
): string | undefined {
  if (!patient) return undefined;
  const mh = patient.medicalHistory;
  const ob = patient.obGynHistory;
  const ls = patient.lifestyle;

  switch (sectionKey) {
    case "basic-info": {
      const parts: string[] = [];
      if (patient.dateOfBirth) {
        const age = Math.floor(
          (Date.now() - new Date(patient.dateOfBirth).getTime()) / 31557600000,
        );
        parts.push(
          t("med_history.overview.recap_age", { params: { age: String(age) } }),
        );
      }
      const bd = patient.bodyData;
      if (bd?.height && bd?.weight) {
        parts.push(`${bd.height}cm, ${bd.weight}kg`);
      } else if (bd?.height) {
        parts.push(`${bd.height}cm`);
      } else if (bd?.weight) {
        parts.push(`${bd.weight}kg`);
      }
      return parts.length > 0 ? parts.join(" · ") : undefined;
    }
    case "infertility": {
      const infertility = patient.fertilityHistory?.infertilityHistory;
      if (infertility) {
        const parts: string[] = [];
        if (infertility.isDiagnosedWithInfertility === true) {
          const causes = infertility.causes;
          if (Array.isArray(causes) && causes.length > 0) {
            const labels = causes.map((c: { cause?: string; other?: string }) =>
              trimSentencePunctuation(
                c.cause === "other" && c.other
                  ? lowerFirst(c.other)
                  : humanizeValue(c.cause),
              ),
            );
            parts.push(`Diagnosis recorded: ${formatList(labels)}`);
          } else {
            parts.push("Infertility diagnosis recorded");
          }
        } else if (infertility.isDiagnosedWithInfertility === false) {
          parts.push(t("med_history.overview.recap_no_diagnosis"));
        }

        if (infertility.isARTNeededForCarrierDisease === true) {
          parts.push("ART need for carrier disease recorded");
        }

        if (infertility.isPartnerInfertile === true) {
          const partnerCause =
            infertility.partnerInfertilityCause ?? infertility.notes;
          parts.push(
            partnerCause
              ? `partner factor: ${lowerFirst(
                  trimSentencePunctuation(humanizeValue(partnerCause)),
                )}`
              : "partner infertility reported",
          );
        } else if (infertility.isPartnerInfertile === false) {
          parts.push("no partner infertility reported");
        }

        return parts.length > 0 ? sentenceCase(parts.join("; ")) : undefined;
      }

      const causes = patient.infertilityCauses;
      if (Array.isArray(causes) && causes.length > 0) {
        const labels = causes.slice(0, 3).map((c: { cause?: string }) => {
          return humanizeValue(c.cause);
        });
        return `Diagnosis recorded: ${formatList(labels)}`;
      }
      return t("med_history.overview.recap_no_diagnosis");
    }
    case "fertility-testing": {
      const results = patient.testResults;
      if (Array.isArray(results) && results.length > 0) {
        const names = results
          .map(
            (result: {
              name?: string | null;
              type?: string | null;
              patientValue?: string | null;
            }) =>
              result.patientValue
                ? `${result.name ?? humanizeValue(result.type)} (${result.patientValue})`
                : (result.name ?? humanizeValue(result.type)),
          )
          .filter(Boolean);
        return `${formatCount(results.length, "test result")} recorded: ${formatList(names)}`;
      }
      return t("med_history.overview.recap_no_test_results");
    }
    case "fertility-treatments": {
      const cycles = patient.cycles;
      if (Array.isArray(cycles) && cycles.length > 0) {
        const types = [
          ...new Set(
            cycles
              .map((c: { cycleType?: string }) => c.cycleType)
              .filter(Boolean),
          ),
        ];
        const outcomes = cycles
          .map(
            (c: {
              outcome?: { type?: string | null; notes?: string | null };
            }) => c.outcome?.notes ?? c.outcome?.type,
          )
          .filter(Boolean);
        const typeText = formatList(types);
        const cycleText = typeText
          ? `${formatCount(cycles.length, "cycle")} recorded: ${typeText}`
          : `${formatCount(cycles.length, "cycle")} recorded`;
        const outcomeText =
          outcomes.length > 0
            ? ` Outcomes include ${formatList(outcomes)}.`
            : "";
        return `${cycleText}.${outcomeText}`;
      }
      return t("med_history.overview.recap_no_treatments");
    }
    case "menstrual-health": {
      const mhData = ob?.menstrualHealth;
      if (!mhData) return undefined;
      const parts: string[] = [];
      if (mhData.cycleStatus) {
        parts.push(sentenceCase(humanizeValue(mhData.cycleStatus)));
      }
      if (mhData.minAverageCycleLength && mhData.maxAverageCycleLength) {
        parts.push(
          `${mhData.minAverageCycleLength}-${mhData.maxAverageCycleLength} day cycles`,
        );
      }
      if (mhData.painScale != null) {
        parts.push(
          t("med_history.overview.recap_pain_scale", {
            params: { value: String(mhData.painScale) },
          }),
        );
      }
      if (mhData.awareOfFertileWindow === true) {
        parts.push("fertile window awareness recorded");
      }
      return parts.length > 0 ? parts.join(", ") : undefined;
    }
    case "sexual-health": {
      const sh = ob?.sexualHealth;
      if (!sh) return undefined;
      const parts: string[] = [];
      if (sh.hadPapSmear === true) {
        const procedures = sh.papSmearProcedures;
        if (Array.isArray(procedures) && procedures.length > 0) {
          parts.push(
            `Pap smear and ${formatList(
              procedures.map((p: { procedure?: string | null }) => p.procedure),
            )} follow-up recorded`,
          );
        } else {
          parts.push(t("med_history.overview.recap_pap_done"));
        }
      }
      if (sh.hadSTDs === true) {
        const stds = sh.stds;
        parts.push(
          Array.isArray(stds) && stds.length > 0
            ? `${formatList(stds.map((std: { stdType?: string | null }) => std.stdType))} history recorded`
            : "STI history recorded",
        );
      } else if (sh.hadSTDs === false) {
        parts.push(t("med_history.overview.recap_no_std"));
      }
      if (
        sh.hasPainfulIntercourse === true &&
        sh.painfulIntercourseScale != null
      ) {
        parts.push(`pain with intercourse ${sh.painfulIntercourseScale}/10`);
      }
      return parts.length > 0 ? parts.join(", ") : undefined;
    }
    case "contraception": {
      const methods = ob?.contraception ?? patient.maleHistory?.contraceptions;
      if (Array.isArray(methods) && methods.length > 0) {
        const names = methods.map(
          (m: {
            method?: string | null;
            contraceptionName?: string | null;
            notes?: string | null;
          }) =>
            m.contraceptionName
              ? `${m.contraceptionName} ${humanizeValue(m.method)}`
              : humanizeValue(m.method),
        );
        const note = methods.find(
          (m: { notes?: string | null }) => m.notes,
        )?.notes;
        return note
          ? `${formatList(names)}; ${lowerFirst(cleanSentenceFragment(note))}`
          : formatList(names);
      }
      if (
        ob?.hasContraceptionHistory === false ||
        patient.maleHistory?.hasContraceptionHistory === false
      ) {
        return t("med_history.overview.recap_no_contraception");
      }
      return undefined;
    }
    case "pregnancies": {
      const preg = ob?.pregnancies;
      if (Array.isArray(preg) && preg.length > 0) {
        const outcomes = preg.map(
          (p: {
            outcome?: string | null;
            deliveryType?: string | null;
            notes?: string | null;
          }) =>
            [humanizeValue(p.outcome), humanizeValue(p.deliveryType)]
              .filter(Boolean)
              .join(" by ") || p.notes,
        );
        return `${formatCount(preg.length, "pregnancy", "pregnancies")} recorded: ${formatList(outcomes)}`;
      }
      if (ob?.hasBeenPregnant === false) {
        return t("med_history.overview.recap_no_pregnancies");
      }
      return undefined;
    }
    case "medications-vaccines": {
      const parts: string[] = [];
      const meds = mh?.medications;
      const vacc = mh?.vaccinations;
      if (Array.isArray(meds) && meds.length > 0) {
        const medNames = meds
          .map(
            (m: {
              name?: string | null;
              brand?: string | null;
              frequency?: string | null;
            }) => m.brand ?? m.name,
          )
          .filter(Boolean);
        parts.push(
          `${formatCount(meds.length, "medication")} recorded: ${formatList(
            medNames,
          )}`,
        );
      } else if (mh?.currentlyTakingMedications === false) {
        parts.push(t("med_history.overview.recap_no_medications"));
      }
      if (Array.isArray(vacc) && vacc.length > 0) {
        const vaccineNames = vacc
          .map((v: { brand?: string | null; type?: string | null }) =>
            [v.brand, humanizeValue(v.type)].filter(Boolean).join(" "),
          )
          .filter(Boolean);
        parts.push(
          `${formatCount(vacc.length, "vaccine")} on file: ${formatList(
            vaccineNames,
          )}`,
        );
      }
      return parts.length > 0 ? parts.join(". ") : undefined;
    }
    case "allergies": {
      const allergies = mh?.allergies;
      if (Array.isArray(allergies) && allergies.length > 0) {
        const names = allergies.map(
          (a: {
            allergy?: string | null;
            name?: string | null;
            comments?: string | null;
          }) =>
            a.name
              ? `${a.name} allergy`
              : `${humanizeValue(a.allergy)} allergy`,
        );
        const comment = allergies.find(
          (a: { comments?: string | null }) => a.comments,
        )?.comments;
        return comment
          ? `${formatList(names)}; ${lowerFirst(cleanSentenceFragment(comment))}`
          : formatList(names);
      }
      if (mh?.hasAllergies === false) {
        return t("med_history.overview.recap_no_allergies");
      }
      return undefined;
    }
    case "diseases": {
      const diseases = mh?.diseases;
      const diseaseNames = Array.isArray(diseases)
        ? diseases.map((d: { disease?: string | null }) =>
            humanizeValue(d.disease),
          )
        : [];
      const cancerNames = Array.isArray(mh?.cancers)
        ? mh.cancers.map((c: { cancer?: string | null }) =>
            humanizeValue(c.cancer),
          )
        : [];
      const breastConditions = Array.isArray(mh?.breastConditions)
        ? mh.breastConditions.map(
            (c: { condition?: string | null; other?: string | null }) =>
              c.condition === "other" && c.other
                ? c.other
                : humanizeValue(c.condition),
          )
        : [];
      const recorded = [
        ...diseaseNames,
        ...cancerNames,
        ...breastConditions,
      ].filter(Boolean);
      if (recorded.length > 0) {
        return `${formatList(recorded, 4)} recorded`;
      }
      if (Array.isArray(diseases) && diseases.length > 0) {
        return t(
          diseases.length === 1
            ? "med_history.overview.recap_conditions_one"
            : "med_history.overview.recap_conditions_other",
          { params: { count: String(diseases.length) } },
        );
      }
      return t("med_history.overview.recap_no_conditions");
    }
    case "surgeries": {
      const surg = mh?.surgeries;
      if (Array.isArray(surg) && surg.length > 0) {
        const names = surg.map(
          (s: {
            surgery?: string | null;
            date?: string | null;
            comments?: string | null;
          }) => {
            const year = getYear(s.date);
            const name = humanizeValue(s.surgery);
            const base = year ? `${name} in ${year}` : name;
            const withComment = s.comments
              ? `${base}; ${lowerFirst(cleanSentenceFragment(s.comments))}`
              : base;
            return sentenceCase(withComment);
          },
        );
        return formatList(names, 2);
      }
      if (mh?.hasSurgeries === false) {
        return t("med_history.overview.recap_no_surgeries");
      }
      return undefined;
    }
    case "exposure": {
      const exp = ls?.exposures;
      if (Array.isArray(exp) && exp.length > 0) {
        const names = exp.map(
          (e: {
            exposure?: string | null;
            frequency?: string | null;
            context?: string | null;
          }) => {
            const base = `${sentenceCase(humanizeValue(e.exposure))} exposure`;
            const frequency = e.frequency
              ? ` (${humanizeValue(e.frequency)})`
              : "";
            return e.context
              ? `${base}${frequency}: ${lowerFirst(
                  cleanSentenceFragment(e.context),
                )}`
              : `${base}${frequency}`;
          },
        );
        return formatList(names, 2);
      }
      if (ls?.hasExposures === false) {
        return t("med_history.overview.recap_no_exposures");
      }
      return undefined;
    }
    case "substances": {
      const add = ls?.addictions;
      if (Array.isArray(add) && add.length > 0) {
        const names = add.map(
          (a: {
            substance?: string | null;
            frequency?: string | null;
            averageQuantity?: number | null;
            notes?: string | null;
          }) => {
            const quantity =
              a.averageQuantity != null
                ? `, about ${a.averageQuantity} per use`
                : "";
            const frequency = a.frequency
              ? ` ${humanizeValue(a.frequency)}`
              : "";
            const note = a.notes
              ? `; ${lowerFirst(cleanSentenceFragment(a.notes))}`
              : "";
            return `${sentenceCase(humanizeValue(a.substance))}${frequency}${quantity}${note}`;
          },
        );
        return formatList(names, 3);
      }
      if (ls?.hasAddictions === false) {
        return t("med_history.overview.recap_no_substances");
      }
      return undefined;
    }
    case "sleep-activity": {
      const parts: string[] = [];
      if (ls?.minHoursOfSleep && ls?.maxHoursOfSleep) {
        parts.push(
          t("med_history.overview.recap_sleep_hours", {
            params: {
              min: String(ls.minHoursOfSleep),
              max: String(ls.maxHoursOfSleep),
            },
          }),
        );
      }
      if (ls?.isExercising === true) {
        if (ls.minHoursOfExercise && ls.maxHoursOfExercise) {
          parts.push(
            `${ls.minHoursOfExercise}-${ls.maxHoursOfExercise}h exercise`,
          );
        } else {
          parts.push(t("med_history.overview.recap_exercises_regularly"));
        }
      } else if (ls?.isExercising === false) {
        parts.push(t("med_history.overview.recap_not_exercising"));
      }
      return parts.length > 0 ? parts.join(", ") : undefined;
    }
    case "diet-supplements": {
      const parts: string[] = [];
      const diets = ls?.diets;
      if (Array.isArray(diets) && diets.length > 0) {
        parts.push(
          `${sentenceCase(
            formatList(
              diets.map((d: { diet?: string | null }) => d.diet),
              2,
            ),
          )} diet`,
        );
      }
      const supps = ls?.dietSupplements;
      if (Array.isArray(supps) && supps.length > 0) {
        const names = supps.map(
          (s: {
            supplement?: string | null;
            frequency?: string | null;
            notes?: string | null;
          }) =>
            s.frequency
              ? `${humanizeValue(s.supplement)} ${humanizeValue(s.frequency)}`
              : humanizeValue(s.supplement),
        );
        parts.push(
          `${formatCount(supps.length, "supplement")}: ${formatList(names)}`,
        );
      }
      return parts.length > 0 ? parts.join(". ") : undefined;
    }
    case "family-history": {
      const fh = patient?.familyHistory;
      if (!fh) return undefined;
      const primaryParts: string[] = [];
      const contextParts: string[] = [];
      const ancestry = fh.ancestryHistory;
      if (Array.isArray(ancestry) && ancestry.length > 0) {
        primaryParts.push(
          `${formatList(
            ancestry.map((a: { ancestry?: string | null }) => a.ancestry),
            2,
          )} ancestry`,
        );
      }
      if (fh.hasDoneDNAAncestryReport === true) {
        contextParts.push("DNA ancestry report recorded");
      }
      if (fh.adoptionStatus === "adopted_or_dont_know_biological_parents") {
        contextParts.push(t("med_history.overview.recap_adopted"));
      } else if (fh.adoptionStatus === "from_gamete_donation") {
        contextParts.push(t("med_history.overview.recap_gamete_donation"));
      }
      if (
        Array.isArray(fh.familyMemberWithFertilityIssues) &&
        fh.familyMemberWithFertilityIssues.length > 0
      ) {
        contextParts.push(
          `fertility issues reported in ${formatList(
            fh.familyMemberWithFertilityIssues.map(
              (member: { familyMember?: string | null }) => member.familyMember,
            ),
          )}`,
        );
      }
      const sentences = [
        primaryParts.join("; "),
        contextParts.length > 0 ? sentenceCase(contextParts.join("; ")) : "",
      ].filter(Boolean);
      return sentences.length > 0 ? sentences.join(". ") : undefined;
    }
    default:
      return undefined;
  }
}

function ensureSentence(text: string): string {
  const trimmed = text.trim();
  if (!trimmed) return trimmed;
  return /[.!?]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

function getSentenceCount(text: string): number {
  return text.match(/[.!?](?=\s|$)/g)?.length ?? 0;
}

function trimSentencePunctuation(text: string): string {
  return text.trim().replace(/[.!?]+$/, "");
}

function getSectionOverviewSubtitle(
  patient: Parameters<typeof getSectionRecap>[0],
  sectionKey: string,
  completion: { percent: number; completed: number; total: number },
  t: TranslationFunction,
): string {
  const recap = getSectionRecap(patient, sectionKey, t);
  const isComplete = completion.percent >= 0.999;
  const hasAnyAnswer = completion.completed > 0;

  if (!isComplete && !hasAnyAnswer) {
    return ensureSentence(
      SECTION_EMPTY_COPY[sectionKey] ??
        "Add this section so Louise has enough context for care planning.",
    );
  }

  if (isComplete) {
    const firstSentence =
      recap ?? SECTION_EMPTY_COPY[sectionKey] ?? "This section is complete.";
    if (
      recap &&
      (recap.length > 90 || getSentenceCount(ensureSentence(recap)) >= 2)
    ) {
      return ensureSentence(firstSentence);
    }
    const secondSentence =
      SECTION_COMPLETE_CONTEXT[sectionKey] ??
      "This gives Louise context for care planning.";
    return `${ensureSentence(firstSentence)} ${ensureSentence(secondSentence)}`;
  }

  const missingSentence =
    SECTION_INCOMPLETE_COPY[sectionKey] ??
    "Finish the remaining answers so this section can support care planning.";

  if (recap) {
    if (recap.length > 90 || getSentenceCount(ensureSentence(recap)) >= 2) {
      return ensureSentence(recap);
    }
    return `${ensureSentence(recap)} ${ensureSentence(missingSentence)}`;
  }

  return ensureSentence(missingSentence);
}

function MedicalHistoryPageHeader({ onBack }: { onBack: () => void }) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicalHistoryPageHeader");

  return (
    <View style={{ marginBottom: 18 }}>
      <View
        style={{
          flexDirection: "row",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 12,
        }}
      >
        <View style={{ flex: 1, minWidth: 0, paddingRight: 4 }}>
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={t("med_history.overview.back")}
            onPress={() => {
              // Chevron-only control; its accessibility label is the copy.
              capturePress(
                pressLabel(t("med_history.overview.back")),
                "med_history.overview.back",
              );
              onBack();
            }}
            hitSlop={8}
            style={({ pressed }) => ({
              alignSelf: "flex-start",
              flexDirection: "row",
              alignItems: "center",
              gap: 3,
              marginBottom: 8,
              opacity: pressed ? 0.62 : 1,
            })}
          >
            <ChevronLeft
              size={17}
              color={EMBER.colors.textLight}
              strokeWidth={2}
            />
            <Text
              className="font-dm-sans"
              style={{
                color: EMBER.colors.textLight,
                fontSize: 14,
                lineHeight: 18,
                fontWeight: "700",
              }}
            >
              {t("med_history.overview.back")}
            </Text>
          </Pressable>
          <ScreenTitle>{t("med_history.overview.header_title")}</ScreenTitle>
          <Text
            accessibilityLabel="Medical History screen title"
            style={{
              position: "absolute",
              width: 1,
              height: 1,
              opacity: 0,
              overflow: "hidden",
            }}
          >
            Medical History
          </Text>
        </View>
        <HeaderActions />
      </View>
      <Text
        className="font-dm-sans"
        numberOfLines={1}
        style={{
          color: EMBER.colors.textLight,
          fontSize: 13.5,
          lineHeight: 19,
          fontWeight: "500",
          marginTop: 10,
          maxWidth: "100%",
        }}
      >
        {t("med_history.overview.header_blurb")}
      </Text>
    </View>
  );
}

function MedicalHistorySyncStatus({
  sectionGroups,
}: {
  sectionGroups: SectionGroup[];
}) {
  const t = useTranslations();
  const capturePress = usePressCapture("MedicalHistorySyncStatus");
  const {
    state,
    pending,
    failed,
    undurable,
    storageError,
    retryFailed,
    beginEditFailed,
    dismissFailed,
  } = usePatientSyncStatus();
  // A failed retry/dismiss keeps the answer parked; say so instead of
  // silently swallowing the rejection.
  const [actionFailed, setActionFailed] = useState(false);
  const retainedFailure = failed[0];
  const visibleFailure = failed.find((failure) => !failure.warningDismissed);
  const actionableFailure = visibleFailure ?? retainedFailure;
  const recoveryRow = actionableFailure?.sectionKey
    ? sectionGroups
        .flatMap((group) => group.rows)
        .find((row) => row.key === actionableFailure.sectionKey)
    : undefined;
  const title =
    state === "checking"
      ? t("med_history.overview.sync_status.title_checking")
      : state === "saving"
        ? t("med_history.overview.sync_status.title_saving")
        : state === "not-synced"
          ? visibleFailure
            ? visibleFailure.reason === "identity"
              ? t("med_history.overview.sync_status.title_session_changed")
              : t("med_history.overview.sync_status.title_needs_attention")
            : failed.length > 0
              ? t("med_history.overview.sync_status.title_retained")
              : t("med_history.overview.sync_status.title_not_synced")
          : t("med_history.overview.sync_status.title_saved");
  const detail =
    undurable > 0
      ? t(
          undurable === 1
            ? "med_history.overview.sync_status.detail_undurable_one"
            : "med_history.overview.sync_status.detail_undurable_other",
          { params: { count: String(undurable) } },
        )
      : storageError
        ? t("med_history.overview.sync_status.detail_storage_error")
        : state === "checking"
          ? t("med_history.overview.sync_status.detail_checking")
          : state === "saving"
            ? t("med_history.overview.sync_status.detail_saving")
            : state === "not-synced"
              ? failed.length > 0
                ? visibleFailure
                  ? visibleFailure.reason === "identity"
                    ? t(
                        "med_history.overview.sync_status.detail_failed_identity",
                      )
                    : t(
                        "med_history.overview.sync_status.detail_failed_visible",
                      )
                  : t("med_history.overview.sync_status.detail_failed_retained")
                : t(
                    pending === 1
                      ? "med_history.overview.sync_status.detail_pending_one"
                      : "med_history.overview.sync_status.detail_pending_other",
                    { params: { count: String(pending) } },
                  )
              : t("med_history.overview.sync_status.detail_saved");
  const accent =
    state === "not-synced"
      ? failed.length > 0
        ? "#A85B35"
        : "#8A673C"
      : "#47745B";

  return (
    <View
      accessibilityLabel={t(
        "med_history.overview.sync_status.accessibility_status",
        {
          params: { title },
        },
      )}
      accessibilityLiveRegion="polite"
      style={{
        flexDirection: "row",
        alignItems: "flex-start",
        gap: 10,
        marginTop: 12,
        borderRadius: 14,
        borderWidth: 1,
        borderColor: `${accent}33`,
        backgroundColor: `${accent}0F`,
        paddingHorizontal: 12,
        paddingVertical: 10,
      }}
    >
      {state === "saving" || state === "checking" ? (
        <ActivityIndicator
          accessibilityLabel={t(
            "med_history.overview.sync_status.accessibility_saving",
          )}
          color={accent}
          size="small"
          style={{ marginTop: 2 }}
        />
      ) : (
        <View
          style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            marginTop: 6,
            backgroundColor: accent,
          }}
        />
      )}
      <View style={{ flex: 1 }}>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.text,
            fontSize: 13,
            lineHeight: 18,
            fontWeight: "700",
          }}
        >
          {title}
        </Text>
        <Text
          className="font-dm-sans"
          style={{
            color: EMBER.colors.textLight,
            fontSize: 12,
            lineHeight: 17,
            fontWeight: "500",
          }}
        >
          {detail}
        </Text>
        {actionableFailure ? (
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 8,
              marginTop: 8,
            }}
          >
            <Pressable
              accessibilityRole="button"
              accessibilityLabel={t(
                "med_history.overview.sync_status.accessibility_retry",
              )}
              onPress={() => {
                capturePress(
                  pressLabel(t("med_history.overview.sync_status.retry")),
                  "med_history.overview.sync_status.retry",
                );
                setActionFailed(false);
                void retryFailed(actionableFailure.id).catch(() =>
                  setActionFailed(true),
                );
              }}
              style={({ pressed }) => ({
                borderRadius: 999,
                borderWidth: 1,
                borderColor: `${accent}55`,
                paddingHorizontal: 10,
                paddingVertical: 5,
                opacity: pressed ? 0.65 : 1,
              })}
            >
              <Text
                className="font-dm-sans"
                style={{ color: accent, fontSize: 12, fontWeight: "700" }}
              >
                {t("med_history.overview.sync_status.retry")}
              </Text>
            </Pressable>
            {recoveryRow?.onPress ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(
                  "med_history.overview.sync_status.edit_section",
                  { params: { section: recoveryRow.title } },
                )}
                onPress={() => {
                  capturePress(
                    pressLabel(
                      t("med_history.overview.sync_status.edit_section", {
                        params: { section: recoveryRow.title },
                      }),
                    ),
                    "med_history.overview.sync_status.edit_section",
                  );
                  setActionFailed(false);
                  if (!beginEditFailed(actionableFailure.id)) {
                    setActionFailed(true);
                    return;
                  }
                  recoveryRow.onPress?.();
                }}
                style={({ pressed }) => ({
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: `${accent}55`,
                  paddingHorizontal: 10,
                  paddingVertical: 5,
                  opacity: pressed ? 0.65 : 1,
                })}
              >
                <Text
                  className="font-dm-sans"
                  style={{ color: accent, fontSize: 12, fontWeight: "700" }}
                >
                  {t("med_history.overview.sync_status.edit_section", {
                    params: { section: recoveryRow.title },
                  })}
                </Text>
              </Pressable>
            ) : null}
            {visibleFailure ? (
              <Pressable
                accessibilityRole="button"
                accessibilityLabel={t(
                  "med_history.overview.sync_status.accessibility_dismiss",
                )}
                onPress={() => {
                  capturePress(
                    pressLabel(t("med_history.overview.sync_status.dismiss")),
                    "med_history.overview.sync_status.dismiss",
                  );
                  setActionFailed(false);
                  void dismissFailed(visibleFailure.id).catch(() =>
                    setActionFailed(true),
                  );
                }}
                style={({ pressed }) => ({
                  paddingHorizontal: 6,
                  paddingVertical: 5,
                  opacity: pressed ? 0.65 : 1,
                })}
              >
                <Text
                  className="font-dm-sans"
                  style={{ color: accent, fontSize: 12, fontWeight: "700" }}
                >
                  {t("med_history.overview.sync_status.dismiss")}
                </Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
        {actionFailed && actionableFailure ? (
          <Text
            className="font-dm-sans"
            accessibilityLiveRegion="polite"
            style={{
              color: accent,
              fontSize: 12,
              lineHeight: 17,
              fontWeight: "500",
              marginTop: 6,
            }}
          >
            {t("med_history.overview.sync_status.action_failed")}
          </Text>
        ) : null}
      </View>
    </View>
  );
}

function MedicalHistoryCareHeroComparison({
  completedCategories,
  totalSections,
  progressPercent,
  nextRow,
  noteDraft,
  noteDraftOpen,
  onAddTextNote,
  onChangeNoteDraft,
  onTellMeAboutMyself,
  onUploadDocument,
}: {
  completedCategories: number;
  totalSections: number;
  progressPercent: number;
  nextRow?: SectionRow;
  noteDraft: string;
  noteDraftOpen: boolean;
  onAddTextNote: () => void;
  onChangeNoteDraft: (value: string) => void;
  onTellMeAboutMyself: () => void;
  onUploadDocument: () => void;
}) {
  const t = useTranslations();
  const sync = usePatientSyncStatus();
  const hasLocalAnswers =
    !sync.hydrated ||
    sync.inFlight ||
    sync.pending > 0 ||
    sync.failed.length > 0 ||
    sync.accepting > 0 ||
    sync.undurable > 0;
  const isComplete = !nextRow && totalSections > 0;
  const isReady = isComplete && !hasLocalAnswers;
  const sectionBenefit = nextRow
    ? (SECTION_BENEFIT_COPY[nextRow.key] ??
      "This section helps Louise personalize next steps and reduce repeated questions.")
    : "Louise can now use your history to personalize alerts, protocol matching, and provider summaries.";
  const heroTitle = isReady
    ? "Your Medical History is ready"
    : isComplete
      ? t("med_history.overview.hero_complete_local_title")
      : nextRow
        ? completedCategories === 0
          ? `Start with ${nextRow.title}`
          : `Next: ${nextRow.title}`
        : "Loading your Medical History";
  const heroSummary = isReady
    ? sectionBenefit
    : isComplete
      ? t("med_history.overview.hero_complete_local_summary")
      : nextRow
        ? sectionBenefit
        : "Louise is checking where to pick up your Medical History.";

  return (
    <Hero
      backgroundSource={careHeroGlowGradient}
      style={{ marginTop: 14 }}
      leading={<ProgressRing percent={progressPercent} size={78} dark />}
      title={heroTitle}
      summary={heroSummary}
      bottomContent={
        <View style={{ marginTop: 16, gap: 12 }}>
          <View
            style={{
              flexDirection: "row",
              alignItems: "center",
              gap: 8,
            }}
          >
            <GlassButton
              fill
              disabled={!nextRow && !isReady}
              label={
                nextRow
                  ? t("med_history.overview.progress_card.continue")
                  : "Tell me about myself"
              }
              accessibilityLabel={
                nextRow
                  ? t(
                      "med_history.overview.progress_card.accessibility_continue",
                    )
                  : "Ask Louise to summarize my Medical History"
              }
              onPress={nextRow?.onPress ?? onTellMeAboutMyself}
              leading={
                nextRow ? (
                  <ArrowRight
                    size={13}
                    color="rgba(255,247,239,0.92)"
                    strokeWidth={2.2}
                  />
                ) : (
                  <Sparkles
                    size={13}
                    color="rgba(255,247,239,0.92)"
                    strokeWidth={2}
                  />
                )
              }
            />
            <GlassButton
              iconOnly
              width={46}
              label="Upload document"
              accessibilityLabel="Upload a Medical History document"
              onPress={onUploadDocument}
              leading={
                <Plus
                  size={11}
                  color="rgba(255,247,239,0.9)"
                  strokeWidth={2.2}
                />
              }
              icon={
                <FilePlus2
                  size={15}
                  color="rgba(255,247,239,0.86)"
                  strokeWidth={1.9}
                />
              }
            />
            <GlassButton
              iconOnly
              width={46}
              label="Text note"
              accessibilityLabel="Add a Medical History text note"
              onPress={onAddTextNote}
              leading={
                <Plus
                  size={11}
                  color="rgba(255,247,239,0.9)"
                  strokeWidth={2.2}
                />
              }
              icon={
                <MessageSquare
                  size={15}
                  color="rgba(255,247,239,0.86)"
                  strokeWidth={1.9}
                />
              }
            />
          </View>
          {noteDraftOpen ? (
            <View
              style={{
                borderRadius: 16,
                borderWidth: 1,
                borderColor: "rgba(255,255,255,0.14)",
                backgroundColor: "rgba(255,255,255,0.09)",
                padding: 12,
                ...(Platform.OS === "web"
                  ? ({
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                    } as Record<string, string>)
                  : null),
              }}
            >
              <TextInput
                className="font-dm-sans"
                multiline
                value={noteDraft}
                onChangeText={onChangeNoteDraft}
                placeholder="Type anything for Louise to parse into Medical History..."
                placeholderTextColor="rgba(255,247,239,0.5)"
                style={{
                  minHeight: 62,
                  color: "#FFFFFF",
                  fontSize: 13,
                  lineHeight: 18,
                  fontWeight: "500",
                }}
              />
              <View style={{ flexDirection: "row", marginTop: 10 }}>
                <GlassButton
                  fill
                  disabled={noteDraft.trim().length === 0}
                  label="Parse note"
                  accessibilityLabel="Parse Medical History note"
                  leading={
                    <Sparkles
                      size={13}
                      color="rgba(255,247,239,0.92)"
                      strokeWidth={2}
                    />
                  }
                />
              </View>
            </View>
          ) : null}
        </View>
      }
    />
  );
}

// Progress ring
function ProgressRing({
  percent,
  size = 80,
  dark = false,
}: {
  percent: number;
  size?: number;
  dark?: boolean;
}) {
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference * (1 - percent / 100);

  return (
    <View style={{ width: size, height: size }}>
      <Svg width={size} height={size}>
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={dark ? "rgba(255,255,255,0.1)" : "rgba(255,255,255,0.15)"}
          strokeWidth={strokeWidth}
          fill="none"
        />
        <Circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke={
            dark
              ? "rgba(255,255,255,0.76)"
              : percent >= 100
                ? EMBER.colors.green
                : EMBER.colors.warm
          }
          strokeWidth={strokeWidth}
          fill="none"
          strokeDasharray={`${circumference}`}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </Svg>
      <View
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        {percent >= 100 ? (
          <Trophy
            size={22}
            color={dark ? "#FFFFFF" : EMBER.colors.green}
            fill={dark ? "#FFFFFF" : EMBER.colors.green}
          />
        ) : (
          <Text
            className="font-dm-sans"
            style={{
              fontSize: 18,
              fontWeight: "700",
              color: dark ? "#FFFFFF" : EMBER.colors.text,
            }}
          >
            {percent}%
          </Text>
        )}
      </View>
    </View>
  );
}

// Section row component — styled like care ListRow
function SectionRowItem({ row }: { row: SectionRow }) {
  const t = useTranslations();
  const isComplete = row.percent >= 0.999;

  return (
    <Row
      surface="plain"
      accessibilityRole="button"
      accessibilityLabel={`Open ${row.title}`}
      onPress={row.onPress}
      title={row.title}
      subtitle={row.recap ?? row.label}
      leading={row.icon}
      leadingSize={28}
      gap={12}
      paddingHorizontal={16}
      paddingVertical={14}
      titleNumberOfLines={1}
      subtitleNumberOfLines={3}
      titleRight={
        isComplete ? (
          <Tag
            label={t("med_history.overview.complete_badge")}
            tone="neutral"
          />
        ) : null
      }
      titleStyle={{
        fontSize: 15,
        lineHeight: 19,
        color: EMBER.colors.text,
        fontWeight: "700",
      }}
      subtitleStyle={{
        fontSize: 12,
        lineHeight: 16,
        color: EMBER.colors.textLight,
        marginTop: 2,
      }}
    />
  );
}

// Section group component
function SectionGroupCard({
  group,
  isFirst = false,
}: {
  group: SectionGroup;
  isFirst?: boolean;
}) {
  const t = useTranslations();
  const completedCount = group.rows.filter((r) => r.percent >= 0.999).length;
  const allComplete = completedCount === group.rows.length;

  return (
    <View style={{ marginTop: isFirst ? 14 : 24 }}>
      <SectionTitleRow
        title={group.title}
        icon={group.icon}
        right={
          allComplete ? (
            <Tag
              label={t("med_history.overview.complete_badge")}
              tone="neutral"
              shape="pill"
            />
          ) : (
            <Tag
              label={`${completedCount}/${group.rows.length}`}
              variant="outline"
              tone="neutral"
              shape="pill"
            />
          )
        }
      />

      {/* Rows card */}
      <View style={{ ...GLASS_CARD_STYLE, padding: 0, overflow: "hidden" }}>
        {group.rows.map((row, i) => (
          <View key={row.key}>
            {i > 0 && (
              <MoonDivider
                color="rgba(255,255,255,0.08)"
                style={{ marginHorizontal: 16 }}
              />
            )}
            <SectionRowItem row={row} />
          </View>
        ))}
      </View>
    </View>
  );
}

export default function MedicalHistoryScreen({ navigation }: Props) {
  const handleBack = useSmartBack({
    fallbackTab: "Care",
    fallbackScreen: "CareOverview",
  });
  const t = useTranslations();
  const [noteDraftOpen, setNoteDraftOpen] = useState(false);
  const [noteDraft, setNoteDraft] = useState("");
  const { data: patient, isLoading } = usePatient();

  const completionData = useMemo(
    () => getMedicalHistoryProgressSummary(patient).completionData,
    [patient],
  );

  const progressSummary = useMemo(
    () => getMedicalHistoryProgressSummary(patient),
    [patient],
  );
  const completedCategories = patient ? progressSummary.completedSections : 0;
  const totalSections = progressSummary.totalSections;
  const progressPercent = patient ? progressSummary.progressPercent : 0;

  const sectionGroups: SectionGroup[] = useMemo(() => {
    const nav = (section: MedicalHistorySection, percent: number) => () => {
      if (percent >= 0.999) {
        // Completed section — show review screen
        navigation.navigate("SectionReview", { sectionKey: section.reviewKey });
      } else {
        navigation.navigate(section.route as MedicalHistoryFlowRoute);
      }
    };

    const ic = (Icon: LucideIcon) => (
      <Icon size={18} color={EMBER.colors.textLight} strokeWidth={1.5} />
    );

    const recap = (
      key: string,
      completion: { percent: number; completed: number; total: number },
    ) => getSectionOverviewSubtitle(patient, key, completion, t);

    return getVisibleMedicalHistoryGroups(patient).map((group) => {
      return {
        key: group.key,
        title: t(group.titleKey),
        icon: group.icon,
        rows: group.sections.map((section) => {
          const completion = completionData[section.key];
          return {
            key: section.reviewKey,
            title: t(section.titleKey),
            icon: ic(section.icon),
            percent: completion.percent,
            label: completion.label,
            recap: recap(section.reviewKey, completion),
            onPress: nav(section, completion.percent),
          };
        }),
      };
    });
  }, [completionData, navigation, patient, t]);

  const nextSectionRow = useMemo(() => {
    for (const group of sectionGroups) {
      const nextRow = group.rows.find((row) => row.percent < 0.999);
      if (nextRow) return nextRow;
    }
    return undefined;
  }, [sectionGroups]);

  return (
    <GlassScreenLayout>
      <ScrollView
        testID="medical-history-overview"
        contentContainerStyle={{
          paddingBottom: 100,
          maxWidth: 520,
          width: "100%",
          alignSelf: "center",
        }}
        showsVerticalScrollIndicator={false}
      >
        {/* ── Dark hero header ── */}
        <View>
          <View
            style={{
              paddingTop: SCREEN_PADDING_TOP,
              paddingHorizontal: 16,
              paddingBottom: 0,
            }}
          >
            <MedicalHistoryPageHeader onBack={handleBack} />
            <MedicalHistorySyncStatus sectionGroups={sectionGroups} />

            <MedicalHistoryCareHeroComparison
              completedCategories={completedCategories}
              totalSections={totalSections}
              progressPercent={isLoading ? 0 : progressPercent}
              nextRow={nextSectionRow}
              noteDraft={noteDraft}
              noteDraftOpen={noteDraftOpen}
              onAddTextNote={() => setNoteDraftOpen((open) => !open)}
              onChangeNoteDraft={setNoteDraft}
              onTellMeAboutMyself={() => {
                const rootNavigation =
                  navigation.getParent<NavigationProp<MainTabParamList>>();
                const state = rootNavigation?.getState() as
                  { routeNames?: string[] } | undefined;
                if (!state?.routeNames?.includes("Chat")) return;
                rootNavigation?.navigate("Chat", {
                  screen: "Assistant",
                  params: {
                    initialPrompt:
                      "Summarize my Medical History and fertility context. Highlight what matters most for care planning, what may need follow-up, and what I should discuss with my provider. Keep it practical and do not make a diagnosis.",
                    autoSendInitialPrompt: true,
                  },
                });
              }}
              onUploadDocument={() => {
                const state = navigation.getState() as {
                  routeNames?: string[];
                };
                if (!state.routeNames?.includes("Documents")) return;
                navigation.navigate("Documents", {
                  initialCategory: "medical_records",
                  openUpload: true,
                  initialDocumentFilterLabel: "Medical History",
                });
              }}
            />
          </View>
        </View>

        {/* Section groups */}
        <View style={{ paddingHorizontal: 16 }}>
          {sectionGroups.map((group, index) => (
            <SectionGroupCard
              key={group.key}
              group={group}
              isFirst={index === 0}
            />
          ))}
        </View>
      </ScrollView>
    </GlassScreenLayout>
  );
}
